import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
/*
createTheme, Material-UI (MUI) kütüphanesinde bir temayı oluşturmak için kullanılan bir fonksiyondur. MUI, React uygulamalarında kullanılan 
popüler bir UI (kullanıcı arayüzü) kütüphanesidir ve createTheme fonksiyonu, uygulamanızın genel görünümünü ve hissini özelleştirmenize olanak
 tanır.
Bu fonksiyon, temelde bir tema nesnesi oluşturur ve bu nesne, uygulama genelinde kullanılacak renkler, tipografi, kenarlık stilleri ve
 diğer birçok stil ayarını içerir. Tema nesnesini özelleştirebilir ve ardından uygulamanızdaki bileşenlere bu temayı uygulayabilirsiniz.
*/
// color design tokens export
export const tokens = (mode) => ({//Her yerde bunu import edeceğim ve bunun üzerinden renklere ulaşacağım.
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          500: "#141b2d",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
      }
    : {
        grey: {
          100: "#1a1a1a",
          200: "#333333",
          300: "#4d4d4d",
          400: "#666666",
          500: "#808080",
          600: "#999999",
          700: "#b3b3b3",
          800: "#cccccc",
          900: "#e6e6e6",
        },
        primary: {
          100: "#f5f2ed",
          200: "#ebe6dd",
          300: "#e0d9cd",
          400: "#f8f5f0", // Card/Container backgrounds (warm cream)
          500: "#ede8df", // Main background (deeper beige)
          600: "#ddd6ca",
          700: "#cac1b3",
          800: "#a59b8d",
          900: "#80776b",
        },
        greenAccent: {
          100: "#064e3b",
          200: "#065f46",
          300: "#047857",
          400: "#059669",
          500: "#10b981",
          600: "#34d399",
          700: "#6ee7b7",
          800: "#a7f3d0",
          900: "#d1fae5",
        },
        redAccent: {
          100: "#7f1d1d",
          200: "#991b1b",
          300: "#b91c1c",
          400: "#dc2626",
          500: "#ef4444",
          600: "#f87171",
          700: "#fca5a5",
          800: "#fecaca",
          900: "#fee2e2",
        },
        blueAccent: {
          100: "#1e3a8a",
          200: "#1e40af",
          300: "#2563eb",
          400: "#3b82f6",
          500: "#60a5fa",
          600: "#93c5fd",
          700: "#bfdbfe",
          800: "#dbeafe",
          900: "#eff6ff",
        },
      }),
});
// mui theme settings
/*
primary: Bu genellikle uygulamanın ana rengidir. Kullanıcı arayüzündeki ana öğeler için kullanılır. main, bu renk paletinin ana tonunu belirtir.

secondary: Bu, ikincil renktir ve genellikle uygulamanın öne çıkan diğer öğeleri veya vurgulanan alanları için kullanılır. main, bu renk paletinin ikinci tonunu belirtir.

neutral: Bu genellikle nötr renkler için kullanılır. Örneğin, gri tonları. dark, main, ve light olmak üzere üç farklı ton içerir.

background: Bu, genellikle uygulamanın genel arka plan rengidir. default, varsayılan arka plan rengini belirtir.
*/
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === "dark";
  return {
    palette: {
      mode,
      primary: {
        main: colors.primary[500],
      },
      secondary: {
        main: colors.greenAccent[500],
      },
      /** tokens.grey indeksleri moda göre: açık arka planda koyu yazı, koyu arka planda açık yazı */
      text: {
        primary: colors.grey[100],
        secondary: colors.grey[400],
      },
      background: {
        default: colors.primary[500],
        paper: isDark ? colors.primary[600] : "#ffffff",
      },
      divider: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(15, 23, 42, 0.08)",
      ...(isDark
        ? {
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
          }
        : {
            neutral: {
              dark: colors.grey[300],
              main: colors.grey[500],
              light: colors.grey[800],
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","), //join kullanmak yerine direkt fontFamily: "Source Sans Pro, sans-serif" şeklinde yazabilirdik.Join bu görünümü sağlar.
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
  };
};

// context for color mode

export const ColorModeContext = createContext({
  toggleColorMode: () => {},//toggleColorMode fonksiyonunda arka planın rengini değişmeyi tetikleyecek fonksiyonu çağıracağım.Bunu createContext içene yazarakta useContext kullanarak sonra buna erişebileceğim.
});

export const useMode = () => {
  const [mode, setMode] = useState("dark");//Başlangıçta sayfam dark olsun dedim

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => //Bu yukarıda createContext içerisine koyduğumuz toggleColorMode fonksiyonudur.Renk değişimi tetikleyeceğiz.
        setMode((prev) => (prev === "light" ? "dark" : "light")), //Sonrasında her değişimde neyse diğeri olsun dedim.
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
  //createTheme içerisindeki themeSettings içerisine mode konulsun dedik Mode değiştikçe yeniden render edilecek.Use memo içerisinde
  //yapmamız maliyetimizin azalmasını sağladı
};
